import { DialogComponent } from '@theme/dialog';
import { supportsViewTransitions, startViewTransition } from '@theme/utilities';

/**
 * A custom element that manages a project popup with view transitions.
 * 
 * @extends {DialogComponent}
 */
class ProjectPopupComponent extends DialogComponent {
  constructor() {
    super();
    this.handleUnifiedTouchStart = this.handleUnifiedTouchStart.bind(this);
    this.handleUnifiedTouchMove = this.handleUnifiedTouchMove.bind(this);
    this.handleUnifiedTouchEnd = this.handleUnifiedTouchEnd.bind(this);
    this.touchStartY = null;
    this.touchCurrentY = null;
    this.touchStartX = null;
    this.touchCurrentX = null;
    this.swipeDirection = null; // 'horizontal', 'vertical', or null
    this.isSwipeActive = false;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // EMERGENCY BACKUP: Listen for dialog close events and force restore scrolling
    if (this.refs.dialog) {
      this.refs.dialog.addEventListener('close', () => {
        setTimeout(() => this.forceRestoreScrolling(), 10);
      });
    }
  }

  /**
   * Shows the dialog with view transition animation
   */
  async showDialog() {
    // MOBILE FIX: Manual body scroll blocking to prevent background scrolling
    this.originalBodyOverflow = document.body.style.overflow;
    this.originalDocumentOverflow = document.documentElement.style.overflow;
    this.originalBodyPosition = document.body.style.position;
    this.originalBodyTop = document.body.style.top;
    this.originalBodyWidth = document.body.style.width;
    
    // Block body scrolling
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Store current scroll position and fix body to that position
    this.scrollY = window.scrollY;
    document.body.style.top = `-${this.scrollY}px`;
    
    const trigger = this.querySelector('.project-popup__trigger');
    const sourceImage = trigger?.querySelector('.project-popup__image');
    
    // For slideshow, get the first slide image; for single image, get the dialog image
    const dialogImage = this.refs.dialog?.querySelector('slideshow-slide .project-popup__dialog-image') || 
                       this.refs.dialog?.querySelector('.project-popup__dialog-image');

    // If view transitions are not supported, fall back to regular dialog
    if (!supportsViewTransitions || !sourceImage || !dialogImage) {
      const result = super.showDialog();
      this.addTouchListeners();
      return result;
    }

    const transitionName = `project-popup-transition`;
    
    // Set transition name on source image
    sourceImage.style.setProperty('view-transition-name', transitionName);

    // Start the view transition
    await startViewTransition(() => {
      // Open the dialog
      super.showDialog();
      
      // Remove transition name from source and add to target
      sourceImage.style.removeProperty('view-transition-name');
      dialogImage.style.setProperty('view-transition-name', transitionName);
    });

    // Clean up transition name after animation
    setTimeout(() => {
      dialogImage.style.removeProperty('view-transition-name');
    }, 100);

    // Add touch listeners for mobile swipe-to-dismiss
    this.addTouchListeners();
  }

  addTouchListeners() {
    // Only add touch listeners on mobile
    if (window.innerWidth <= 749 && this.refs.dialog) {
      // Use unified touch handlers that handle both horizontal and vertical swipes
      this.refs.dialog.addEventListener('touchstart', this.handleUnifiedTouchStart, { passive: true });
      this.refs.dialog.addEventListener('touchmove', this.handleUnifiedTouchMove, { passive: false });
      this.refs.dialog.addEventListener('touchend', this.handleUnifiedTouchEnd, { passive: true });
    }

    // Add slideshow button listeners
    this.addSlideshowListeners();
  }

  addSlideshowListeners() {
    const slideshow = this.refs.dialog?.querySelector('slideshow-component');
    const slides = this.refs.dialog?.querySelectorAll('slideshow-slide');
    const prevButton = this.refs.dialog?.querySelector('[data-direction="previous"]');
    const nextButton = this.refs.dialog?.querySelector('[data-direction="next"]');

    if (slides && slides.length > 1 && prevButton && nextButton) {
      // Initializing slideshow
      this.currentSlideIndex = 0;
      this.totalSlides = slides.length;
      
      // Remove existing event listeners first to prevent duplicates
      prevButton.removeEventListener('click', this.handlePrevious);
      nextButton.removeEventListener('click', this.handleNext);
      
      // Set up the slides container width based on number of slides
      const slidesContainer = this.refs.dialog?.querySelector('slideshow-slides');
      if (slidesContainer) {
        // Container should be totalSlides * 100% to fit all slides
        slidesContainer.style.width = `${this.totalSlides * 100}%`;
        
        // Each slide should be 100% of viewport width, which is 100/totalSlides% of container width
        slides.forEach(slide => {
          slide.style.width = `${100 / this.totalSlides}%`;
          slide.style.flexShrink = '0'; // Prevent slides from shrinking
        });
        
        // Container and slide dimensions configured
      }
      
      this.handlePrevious = () => {
        const oldIndex = this.currentSlideIndex;
        this.currentSlideIndex = (this.currentSlideIndex - 1 + this.totalSlides) % this.totalSlides;
        console.log(`Previous: ${oldIndex} -> ${this.currentSlideIndex}`);
        this.showSlide(this.currentSlideIndex);
        // Remove focus from button to prevent lingering focus outline on mobile
        if (document.activeElement === prevButton) {
          prevButton.blur();
        }
      };
      
      this.handleNext = () => {
        const oldIndex = this.currentSlideIndex;
        this.currentSlideIndex = (this.currentSlideIndex + 1) % this.totalSlides;
        console.log(`Next: ${oldIndex} -> ${this.currentSlideIndex}`);
        this.showSlide(this.currentSlideIndex);
        // Remove focus from button to prevent lingering focus outline on mobile
        if (document.activeElement === nextButton) {
          nextButton.blur();
        }
      };
      
      prevButton.addEventListener('click', this.handlePrevious);
      nextButton.addEventListener('click', this.handleNext);
      
      console.log('Event listeners added, initializing first slide');
      // Initialize first slide
      this.showSlide(0);
    }
  }

  showSlide(index) {
    const slides = this.refs.dialog?.querySelectorAll('slideshow-slide');
    const slidesContainer = this.refs.dialog?.querySelector('slideshow-slides');
    
    if (slides && slidesContainer) {
      console.log(`Showing slide ${index} of ${this.totalSlides} total slides`);
      
      // Each slide takes 100/totalSlides% of container width
      // So we need to move by that percentage for each slide
      const slideWidthPercent = 100 / this.totalSlides;
      const translateX = -index * slideWidthPercent;
      console.log(`Transform: translateX(${translateX}%) (slide width: ${slideWidthPercent}%)`);
      slidesContainer.style.transform = `translateX(${translateX}%)`;
      
      // Update aria-hidden attributes for accessibility
      slides.forEach((slide, i) => {
        if (i === index) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }

  removeTouchListeners() {
    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener('touchstart', this.handleUnifiedTouchStart);
      this.refs.dialog.removeEventListener('touchmove', this.handleUnifiedTouchMove);
      this.refs.dialog.removeEventListener('touchend', this.handleUnifiedTouchEnd);
    }

    // Remove slideshow listeners
    const prevButton = this.refs.dialog?.querySelector('[data-direction="previous"]');
    const nextButton = this.refs.dialog?.querySelector('[data-direction="next"]');
    
    if (prevButton) {
      prevButton.removeEventListener('click', this.handlePrevious);
    }
    if (nextButton) {
      nextButton.removeEventListener('click', this.handleNext);
    }
  }

  // Unified touch handlers that handle both horizontal and vertical swipes
  handleUnifiedTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchCurrentX = this.touchStartX;
    this.touchCurrentY = this.touchStartY;
    this.swipeDirection = null;
    this.isSwipeActive = false;
    
    // Touch tracking initialized - no visual feedback during swipe
    

  }

  handleUnifiedTouchMove(event) {
    if (!this.touchStartX || !this.touchStartY) return;
    
    this.touchCurrentX = event.touches[0].clientX;
    this.touchCurrentY = event.touches[0].clientY;
    
    const deltaX = Math.abs(this.touchCurrentX - this.touchStartX);
    const deltaY = Math.abs(this.touchCurrentY - this.touchStartY);
    
    // Determine swipe direction if not already determined
    if (!this.swipeDirection && (deltaX > 10 || deltaY > 10)) {
      if (deltaX > deltaY) {
        this.swipeDirection = 'horizontal';
                // Horizontal swipe detected
       } else {
        this.swipeDirection = 'vertical';
        // Vertical swipe detected
      }
      this.isSwipeActive = true;
    }
    
    if (this.swipeDirection === 'horizontal' && this.totalSlides > 1) {
      // Handle horizontal swipe for slideshow navigation
      event.preventDefault();
      
      const slidesContainer = this.refs.dialog?.querySelector('slideshow-slides');
      if (slidesContainer && deltaX > 20) {
        const translateX = (this.touchCurrentX - this.touchStartX) * 0.1;
        slidesContainer.style.transition = 'none';
        slidesContainer.style.transform = `translateX(calc(-${this.currentSlideIndex * (100 / this.totalSlides)}% + ${translateX}px))`;
      }
    } else if (this.swipeDirection === 'vertical') {
      // Handle vertical swipe for dialog dismissal
      // No visual feedback during swipe - just detect the direction
      // The actual closing animation will happen in handleUnifiedTouchEnd
    }
  }

  handleUnifiedTouchEnd(event) {
    if (!this.touchStartX || !this.touchStartY || !this.isSwipeActive) {
      this.resetTouchState();
      return;
    }
    
    if (this.swipeDirection === 'horizontal' && this.totalSlides > 1) {
      // Handle horizontal swipe end for slideshow
      const touchDiff = this.touchCurrentX - this.touchStartX;
      const threshold = 50;
      
      const slidesContainer = this.refs.dialog?.querySelector('slideshow-slides');
      if (slidesContainer) {
        slidesContainer.style.transition = 'transform 0.3s ease';
      }
      
      if (Math.abs(touchDiff) > threshold) {
        if (touchDiff > 0) {
          console.log('Swipe triggered: Previous');
          if (this.handlePrevious) {
            this.handlePrevious();
          }
        } else {
          console.log('Swipe triggered: Next');
          if (this.handleNext) {
            this.handleNext();
          }
        }
        // Remove focus from any slideshow button after swipe
        const activeElement = document.activeElement;
        if (activeElement && activeElement.classList.contains('slideshow-control')) {
          activeElement.blur();
        }
      } else {
        this.showSlide(this.currentSlideIndex);
      }
    } else if (this.swipeDirection === 'vertical') {
      // Handle vertical swipe end for dialog dismissal
      const touchDiff = this.touchCurrentY - this.touchStartY;
      const threshold = 100;
      
      // Only close if swiped down beyond threshold
      if (touchDiff > threshold) {
        // Enable smooth closing transition - schneller für bessere UX
        this.refs.dialog.style.transition = 'transform 0.15s ease-out, opacity 0.15s ease-out';
        
        // Animate out and close
        this.refs.dialog.style.transform = 'translateY(100vh)';
        this.refs.dialog.style.opacity = '0';
        
        setTimeout(() => {
          this.closeDialog();
        }, 150); // Reduziert von 300ms auf 150ms
      }
      // No need for snap-back since there was no visual feedback during swipe
    }
    
    this.resetTouchState();
  }

  resetTouchState() {
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchCurrentX = null;
    this.touchCurrentY = null;
    this.swipeDirection = null;
    this.isSwipeActive = false;
  }

  /**
   * Nuclear option: Force restore scrolling by resetting everything
   */
  forceRestoreScrolling() {
    // Remove ALL possible style properties that could block scrolling
    const elementsToReset = [document.body, document.documentElement];
    
    elementsToReset.forEach(element => {
      element.style.removeProperty('overflow');
      element.style.removeProperty('overflow-x');
      element.style.removeProperty('overflow-y');
      element.style.removeProperty('position');
      element.style.removeProperty('top');
      element.style.removeProperty('left');
      element.style.removeProperty('width');
      element.style.removeProperty('height');
      element.style.removeProperty('max-width');
      element.style.removeProperty('max-height');
      element.style.removeProperty('-webkit-overflow-scrolling');
      
      // Force reset to normal scrolling
      element.style.overflow = '';
      element.style.position = '';
    });
    
    // Also check for any class-based scroll locks
    document.body.classList.remove('scroll-lock', 'no-scroll', 'dialog-open');
    document.documentElement.classList.remove('scroll-lock', 'no-scroll', 'dialog-open');
    
    // Restore scroll position if we saved it
    if (this.scrollY !== undefined) {
      setTimeout(() => {
        window.scrollTo(0, this.scrollY);
        // Double-check after a moment
        setTimeout(() => {
          window.scrollTo(0, this.scrollY);
        }, 100);
      }, 10);
    }
    
    // Reset our stored values
    this.scrollY = undefined;
    this.originalBodyOverflow = undefined;
    this.originalDocumentOverflow = undefined;
    this.originalBodyPosition = undefined;
    this.originalBodyTop = undefined;
    this.originalBodyWidth = undefined;
    
    console.log('Force restored scrolling');
  }



  /**
   * Closes the dialog with view transition animation
   */
  async closeDialog() {
    // NUCLEAR OPTION: Force restore scrolling no matter what
    this.forceRestoreScrolling();
    
    // Remove touch listeners
    this.removeTouchListeners();
    
    const trigger = this.querySelector('.project-popup__trigger');
    const sourceImage = trigger?.querySelector('.project-popup__image');
    
    // Get the currently active slide image (for slideshow) or the single dialog image
    const dialogImage = this.refs.dialog?.querySelector('slideshow-slide:not([aria-hidden="true"]) .project-popup__dialog-image') || 
                       this.refs.dialog?.querySelector('slideshow-slide .project-popup__dialog-image') ||
                       this.refs.dialog?.querySelector('.project-popup__dialog-image');

    // If view transitions are not supported, fall back to regular dialog
    if (!supportsViewTransitions || !sourceImage || !dialogImage) {
      const result = super.closeDialog();
      // Force restore scrolling again after parent close
      setTimeout(() => this.forceRestoreScrolling(), 50);
      return result;
    }

    const transitionName = `project-popup-transition`;
    
    // Set transition name on dialog image first
    dialogImage.style.setProperty('view-transition-name', transitionName);

    // Start the view transition - this creates the seamless morphing effect
    await startViewTransition(() => {
      // Remove transition name from dialog image
      dialogImage.style.removeProperty('view-transition-name');
      
      // Set transition name on source image (this is the destination)
      sourceImage.style.setProperty('view-transition-name', transitionName);
      
      // Close the dialog
      super.closeDialog();
    });

    // Clean up transition name after animation completes
    setTimeout(() => {
      sourceImage.style.removeProperty('view-transition-name');
      // Final backup restore
      this.forceRestoreScrolling();
    }, 150);
  }
}

// Register the custom element
if (!customElements.get('project-popup-component')) {
  customElements.define('project-popup-component', ProjectPopupComponent);
}

// Keep backward compatibility with image-popup-component for any existing usage
class ImagePopupComponent extends ProjectPopupComponent {}

if (!customElements.get('image-popup-component')) {
  customElements.define('image-popup-component', ImagePopupComponent);
}

export { ProjectPopupComponent, ImagePopupComponent }; 